
import React from "react";
import { companyInfo } from "../data/mockData";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Phone, MessageCircle, CheckCircle2, Star, Car, Bike, Truck, Shield, Wrench, MapPin, Clock, ShieldCheck } from "lucide-react";
import Layout from "../components/Layout";
import { useLanguage } from "../context/LanguageContext";

// Map string icon names to components
const iconMap = {
  Car: Car,
  Bike: Bike,
  Truck: Truck,
  Shield: Shield,
  Wrench: Wrench,
  MapPin: MapPin,
};

const iconList = [Car, Bike, Truck, Shield, Wrench, MapPin];

// Image Assets (Using the user provided public URLs)
const images = {
  hero: "https://customer-assets.emergentagent.com/job_gruas-asistencia/artifacts/4p387t88_Gemini_Generated_Image_y0go71y0go71y0go.png",
  service1: "https://customer-assets.emergentagent.com/job_db6e5103-6c6e-4ef0-8501-fc9dc03f1736/artifacts/lk9odxz6_d0ff3c71-e3ab-4373-b107-161437a93de8.jpg",
  service2: "https://customer-assets.emergentagent.com/job_db6e5103-6c6e-4ef0-8501-fc9dc03f1736/artifacts/8bf24jdt_2ff335db-4a33-43a0-a06b-0b3e06978ad0.jpg",
  service3: "https://customer-assets.emergentagent.com/job_db6e5103-6c6e-4ef0-8501-fc9dc03f1736/artifacts/zpxn9q5j_cd949c70-3056-4b20-9386-a87c26000b14.jpg",
  largeTruck: "https://customer-assets.emergentagent.com/job_db6e5103-6c6e-4ef0-8501-fc9dc03f1736/artifacts/w923mx8i_ce1cf2ad-b38e-4e49-b40e-56b5f5ba4bc4.jpg",
};

const Home = () => {
  const { t } = useLanguage();

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative w-full min-h-[600px] h-auto md:h-[700px] overflow-hidden flex items-center py-20 md:py-0">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src={images.hero} 
            alt="Grúa en Zaragoza Asistencia" 
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b md:bg-gradient-to-r from-black/90 via-black/70 to-black/30 md:to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-4 h-full flex flex-col justify-center">
          <div className="max-w-2xl text-white space-y-6 animate-in slide-in-from-left-5 md:slide-in-from-left-10 duration-700 fade-in text-center md:text-left">
            <div className="inline-flex items-center gap-2 bg-primary/90 text-primary-foreground px-4 py-1.5 rounded-full text-sm font-semibold mb-2 backdrop-blur-sm shadow-lg mx-auto md:mx-0">
              <Clock className="w-4 h-4" /> {t.hero.badge}
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-6xl font-extrabold leading-tight tracking-tight drop-shadow-lg">
              {t.hero.title} <br/>
              <span className="text-primary">{t.hero.subtitle}</span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-slate-100 leading-relaxed max-w-xl mx-auto md:mx-0 drop-shadow-md">
              {t.hero.description}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 pt-4 justify-center md:justify-start">
              <Button size="lg" className="w-full sm:w-auto text-lg px-8 h-14 gap-2 shadow-xl hover:scale-105 transition-transform" asChild>
                <a href={`tel:${companyInfo.phone}`}>
                  <Phone className="w-5 h-5" /> {t.hero.ctaPrimary}
                </a>
              </Button>
              <Button size="lg" variant="secondary" className="w-full sm:w-auto text-lg px-8 h-14 gap-2 shadow-xl hover:scale-105 transition-transform bg-white text-slate-900 hover:bg-slate-100" asChild>
                <a href={`https://wa.me/${companyInfo.whatsapp.replace('+', '')}`}>
                  <MessageCircle className="w-5 h-5 text-green-600" /> {t.hero.ctaSecondary}
                </a>
              </Button>
            </div>

            <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 sm:gap-6 pt-8 text-sm font-medium text-slate-200">
              <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-primary" /> {t.hero.badge1}
              </div>
              <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-primary" /> {t.hero.badge2}
              </div>
              <div className="flex items-center gap-2 bg-black/30 px-3 py-1 rounded-full backdrop-blur-sm">
                <CheckCircle2 className="w-5 h-5 text-primary" /> {t.hero.badge3}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intro/Trust Section */}
      <section className="py-12 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
                {t.trust.title} <span className="text-primary">{t.trust.location}</span>
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                <strong>{companyInfo.name}</strong>, {t.trust.paragraph1}
              </p>
              <p className="text-muted-foreground text-lg leading-relaxed">
                {t.trust.paragraph2}
              </p>
              
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="bg-secondary/50 p-4 rounded-xl border border-border">
                  <h3 className="font-bold text-xl mb-1 text-primary">{t.trust.stat1}</h3>
                  <p className="text-sm text-muted-foreground">{t.trust.stat1Text}</p>
                </div>
                <div className="bg-secondary/50 p-4 rounded-xl border border-border">
                  <h3 className="font-bold text-xl mb-1 text-primary">{t.trust.stat2}</h3>
                  <p className="text-sm text-muted-foreground">{t.trust.stat2Text}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-primary/10 rounded-3xl -z-10 transform rotate-3"></div>
              <img 
                src={images.service1} 
                alt="Grúa cargando coche BMW" 
                className="rounded-2xl shadow-2xl w-full object-cover h-[300px] md:h-[400px]"
              />
              {/* Trust Badge - Responsive */}
              <div className="mt-4 md:mt-0 md:absolute md:-bottom-6 md:-left-6 bg-white p-6 rounded-xl shadow-xl border md:max-w-xs w-full">
                <div className="flex items-center gap-3 mb-2">
                  <div className="bg-green-100 p-2 rounded-full flex-shrink-0">
                    <ShieldCheck className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-slate-900">{t.trust.insuranceTitle}</p>
                    <p className="text-xs text-slate-500">{t.trust.insuranceText}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section id="services" className="py-16 md:py-20 bg-secondary/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t.services.title}</h2>
            <p className="text-lg text-muted-foreground">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {t.services.items.map((service, index) => {
              const Icon = iconList[index];
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-xl transition-shadow duration-300">
                  <CardHeader>
                    <div className="w-12 h-12 bg-primary/10 text-primary rounded-lg flex items-center justify-center mb-4">
                      {Icon && <Icon className="w-6 h-6" />}
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-base text-slate-600">
                      {service.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Large Image Showcase */}
          <div className="mt-12 md:mt-16 grid md:grid-cols-2 gap-4">
             <img 
                src={images.service2} 
                alt="Transporte de furgoneta" 
                className="rounded-xl shadow-lg w-full h-48 md:h-64 object-cover hover:scale-[1.02] transition-transform duration-300"
              />
              <img 
                src={images.largeTruck} 
                alt="Grúa camión grande" 
                className="rounded-xl shadow-lg w-full h-48 md:h-64 object-cover hover:scale-[1.02] transition-transform duration-300"
              />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="process" className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-12 items-center">
            <div className="w-full md:w-1/2 order-2 md:order-1">
               <img 
                src={images.service3} 
                alt="Proceso de solicitud de grúa" 
                className="rounded-2xl shadow-2xl w-full object-cover max-h-[500px]"
              />
            </div>
            <div className="w-full md:w-1/2 space-y-8 order-1 md:order-2">
              <h2 className="text-3xl md:text-4xl font-bold">
                {t.process.title} <br/>{t.process.titleEnd}
              </h2>
              <p className="text-muted-foreground text-lg">
                {t.process.subtitle} <strong>{companyInfo.name}</strong>.
              </p>

              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">1</div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">{t.process.step1Title}</h3>
                    <p className="text-muted-foreground">{t.process.step1Text} <a href={`tel:${companyInfo.phone}`} className="text-primary font-medium hover:underline">{companyInfo.phone}</a> {t.process.step1TextEnd}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">2</div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">{t.process.step2Title}</h3>
                    <p className="text-muted-foreground">{t.process.step2Text}</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-lg">3</div>
                  <div>
                    <h3 className="font-bold text-xl mb-2">{t.process.step3Title}</h3>
                    <p className="text-muted-foreground">{t.process.step3Text}</p>
                  </div>
                </div>
              </div>

              <div className="pt-4">
                <Button size="lg" className="w-full md:w-auto gap-2 text-lg h-14" asChild>
                  <a href={`tel:${companyInfo.phone}`}>
                    <Phone className="w-5 h-5" /> {t.process.cta}
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form */}

      {/* Testimonials */}
      <section id="reviews" className="py-16 md:py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="flex">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="font-bold text-xl">{t.reviews.rating}</span>
            </div>
            <h2 className="text-3xl font-bold mb-4">{t.reviews.title}</h2>
            <p className="text-muted-foreground">{t.reviews.subtitle}</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {t.reviews.items.slice(0, 8).map((review, index) => (
              <Card key={index} className="bg-white border-slate-100 shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between mb-2">
                     <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center text-slate-500 font-bold">
                        {review.name.charAt(0)}
                     </div>
                     <svg className="w-5 h-5 opacity-60" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                       <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                       <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                       <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                       <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                     </svg>
                  </div>
                  <CardTitle className="text-base font-semibold">{review.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex mb-2">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 italic">"{review.text}"</p>
                  <p className="text-xs text-slate-400 mt-3">{review.date}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Leave a Review Button */}
          <div className="mt-12 text-center">
            <a 
              href="https://www.google.com/maps/place/CLS+Logistic+Activ/@41.5762783,-0.8376789,17z/data=!4m6!3m5!1s0xd593d47c95c4ff9:0xb1114631799ab39f!8m2!3d41.5745346!4d-0.8253293!16s%2Fg%2F11x8g8wb5d?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-white border-2 border-slate-200 hover:border-primary hover:shadow-lg px-8 py-4 rounded-xl transition-all duration-300 group"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <div className="text-left">
                <p className="font-bold text-slate-900 group-hover:text-primary transition-colors">
                  {t.reviews.leaveReview}
                </p>
                <p className="text-sm text-slate-500">{t.reviews.leaveReviewSub}</p>
              </div>
              <div className="flex ml-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-16 md:py-20 bg-background">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">{t.faq.title}</h2>
          <Accordion type="single" collapsible className="w-full">
            {t.faq.items.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-left font-semibold text-lg hover:text-primary hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Sticky Bottom Call Button (Mobile) */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t p-4 flex gap-3 z-50 pb-safe shadow-[0_-5px_10px_rgba(0,0,0,0.05)]">
        <Button className="flex-1 gap-2 bg-primary text-primary-foreground h-12" size="lg" asChild>
          <a href={`tel:${companyInfo.phone}`}>
            <Phone className="w-5 h-5" /> {t.common.llamar}
          </a>
        </Button>
        <Button variant="outline" className="flex-1 gap-2 border-green-600 text-green-600 hover:bg-green-50 h-12" size="lg" asChild>
          <a href={`https://wa.me/${companyInfo.whatsapp.replace('+', '')}`}>
             <MessageCircle className="w-5 h-5" /> WhatsApp
          </a>
        </Button>
      </div>
    </Layout>
  );
};

export default Home;
